import * as React from 'react'

export interface CreateUserTab {
  id: string
  label: string
  panel: React.ReactNode
}

export interface CreateUserTabsProps {
  tabs: CreateUserTab[]
  /** Accessible name for the tab list. */
  ariaLabel?: string
}

/**
 * The Invite / Import tab shell shared by the two Create User surfaces.
 *
 * The tab bar is built from Momentum's CSS classes rather than from its `Tabs`
 * component because `mds-c-tabs` makes the wrapper a flex column and
 * `mds-c-tabs__section` makes the panel flex-grow, which together push the nav out of
 * the sidebar on these screens. This markup keeps the classes that style a tab and
 * omits the two that lay one out. (It previously also existed because `Tabs` has no
 * disabled-tab support, which is what held the Import placeholder — that is no longer
 * needed now the tab does something: a tab the viewer may not use is not rendered at
 * all rather than rendered dead.)
 *
 * Because it is hand-rolled it also owns its own keyboard behaviour. The tab list is
 * one tab stop — only the selected tab is in the tab order — so the arrow keys are what
 * move between tabs, and without them an unselected tab is reachable by pointer only.
 * Selection follows focus, which is the expected behaviour when switching costs nothing
 * and each panel is already rendered.
 *
 * With a single tab there is nothing to switch between, so only the panel renders.
 */
export const CreateUserTabs = ({
  tabs,
  ariaLabel,
}: CreateUserTabsProps): React.ReactElement | null => {
  const [activeId, setActiveId] = React.useState<string | undefined>(
    tabs[0]?.id,
  )
  const tabRefs = React.useRef<Record<string, HTMLAnchorElement | null>>({})

  // A tab can disappear between renders — the Import tab appears only once the
  // signed-in user is known to be an admin — so fall back rather than render nothing.
  const active = tabs.find(tab => tab.id === activeId) ?? tabs[0]

  const select = React.useCallback((tab: CreateUserTab) => {
    setActiveId(tab.id)
    // The newly selected tab is the one in the tab order, so focus has to follow it or
    // the next Tab press would leave the list from a tab that is no longer a tab stop.
    tabRefs.current[tab.id]?.focus()
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const last = tabs.length - 1
    let next: number | undefined
    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1
    else if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = last
    if (next === undefined) return
    event.preventDefault()
    select(tabs[next])
  }

  if (!tabs.length) return null

  if (tabs.length === 1) {
    return (
      <div
        id={`${active.id}-panel`}
        role='tabpanel'
        aria-labelledby={`${active.id}-tab`}
      >
        {active.panel}
      </div>
    )
  }

  return (
    <div>
      <ul className='mds-c-tabs__list' role='tablist' aria-label={ariaLabel}>
        {tabs.map((tab, index) => {
          const isActive = tab.id === active.id
          return (
            <li className='mds-c-tab' role='presentation' key={tab.id}>
              <a
                className={`mds-c-tab__link${isActive ? ' mds-is-active' : ''}`}
                href={`#${tab.id}-panel`}
                id={`${tab.id}-tab`}
                ref={element => {
                  tabRefs.current[tab.id] = element
                }}
                role='tab'
                aria-selected={isActive}
                aria-controls={`${tab.id}-panel`}
                tabIndex={isActive ? 0 : -1}
                onClick={e => {
                  e.preventDefault()
                  select(tab)
                }}
                onKeyDown={e => handleKeyDown(e, index)}
              >
                {tab.label}
              </a>
            </li>
          )
        })}
      </ul>
      {/* Every panel stays mounted and the inactive ones are hidden, rather than only
          the selected one being rendered. Unmounting would throw away whatever the
          admin had in the panel they left — a long list of problems to work through, or
          a half-typed invite — and it would also leave each inactive tab's
          `aria-controls` pointing at an element that does not exist, so a screen
          reader's "go to the controlled element" command lands nowhere. */}
      {tabs.map(tab => (
        <div
          key={tab.id}
          id={`${tab.id}-panel`}
          role='tabpanel'
          aria-labelledby={`${tab.id}-tab`}
          hidden={tab.id !== active.id}
        >
          {tab.panel}
        </div>
      ))}
    </div>
  )
}
